import { useMemo } from 'react';
import { Batiment, Personnel, Vehicule } from '../../../../shared/types';
import { type InstanceRow } from '../../components/interventions/instanceModel';
import { isCtaBuildingType, unitTypeLabel } from '../mapUtils';

interface UseMapMetricsParams {
  buildings: Batiment[];
  playerVehicles: Vehicule[];
  playerPersonnel: Personnel[];
  activeInterventions: InstanceRow[];
  selectedIntervention: InstanceRow | null;
}

/**
 * Métriques de la carte. Ne prend plus les filtres d'unités (Étape 2b) : la
 * construction des lignes filtrées est passée dans `map/unitsRows.ts`, pour que
 * chaque vue garde ses filtres en état LOCAL au lieu de les partager via `Map.tsx`.
 */
export function useMapMetrics({
  buildings,
  playerVehicles,
  playerPersonnel,
  activeInterventions,
  selectedIntervention,
}: UseMapMetricsParams) {
  const vehicleStatusMetrics = useMemo(() => {
    const total = playerVehicles.length;
    const pret = playerVehicles.filter((v) => String(v.statut) === 'disponible').length;
    const mission = playerVehicles.filter((v) => String(v.statut) === 'en_mission').length;
    const maintenance = playerVehicles.filter((v) => String(v.statut) === 'en_maintenance').length;
    const panne = playerVehicles.filter((v) => String(v.statut) === 'panne').length;
    const premium = playerVehicles.filter((v) => Boolean(v.premium)).length;
    const totalKm = playerVehicles.reduce(
      (sum, v) => sum + Math.max(0, Number(v.kilometrage || 0)),
      0,
    );
    const avgKm = total > 0 ? Math.round(totalKm / total) : 0;
    const totalCapacity = playerVehicles.reduce(
      (sum, v) => sum + Math.max(1, Number(v.capacite || 2)),
      0,
    );
    return {
      total,
      pret,
      mission,
      maintenance,
      panne,
      premium,
      totalKm,
      avgKm,
      totalCapacity,
      readinessPercent: total > 0 ? Math.round((pret / total) * 100) : 0,
    };
  }, [playerVehicles]);

  const personnelMetrics = useMemo(() => {
    const total = playerPersonnel.length;
    const actif = playerPersonnel.filter((p) => Boolean(p.actif)).length;
    const inactif = Math.max(0, total - actif);
    const avgAge =
      total > 0
        ? Math.round(
            playerPersonnel.reduce((sum, p) => sum + Math.max(0, Number(p.age || 0)), 0) / total,
          )
        : 0;
    const nearRetirement = playerPersonnel.filter(
      (p) => Number(p.age_retraite || 0) - Number(p.age || 0) <= 5,
    ).length;
    const premium = playerPersonnel.filter((p) => Boolean(p.premium)).length;
    return {
      total,
      actif,
      inactif,
      avgAge,
      nearRetirement,
      premium,
      activeRate: total > 0 ? Math.round((actif / total) * 100) : 0,
    };
  }, [playerPersonnel]);

  const buildingOperationalRows = useMemo(() => {
    return buildings
      .filter((b) => !isCtaBuildingType(b.type))
      .map((building) => {
        const linkedVehicles = playerVehicles.filter(
          (v) => Number(v.batiment_id || 0) === Number(building.id),
        );
        const linkedPersonnel = playerPersonnel.filter(
          (p) => Number(p.batiment_id || 0) === Number(building.id),
        );
        const vehCapacity = Math.max(0, Number(building.capacite_vehicules || 0));
        const personnelSlots = Math.max(0, Number(building.postes_disponibles || 0));
        const personnelOccupied = Math.max(0, Number(building.postes_occupes || 0));
        const vehicleLoadPercent =
          vehCapacity > 0
            ? Math.min(100, Math.round((linkedVehicles.length / vehCapacity) * 100))
            : 0;
        const personnelLoadPercent =
          personnelSlots > 0
            ? Math.min(100, Math.round((personnelOccupied / personnelSlots) * 100))
            : 0;
        const xp = Math.max(0, Number(building.xp || 0));
        const xpRequired = Math.max(1, Number(building.xp_requis_par_niveau || 10));
        return {
          id: building.id,
          name: building.nom || `Bâtiment ${building.id}`,
          typeLabel: unitTypeLabel(building.type),
          level: Number(building.niveau || 1),
          vehiclesCount: linkedVehicles.length,
          vehiclesReady: linkedVehicles.filter((v) => String(v.statut) === 'disponible').length,
          vehicleCapacity: vehCapacity,
          vehicleLoadPercent,
          personnelCount: linkedPersonnel.length,
          personnelActive: linkedPersonnel.filter((p) => p.actif).length,
          personnelSlots,
          personnelOccupied,
          personnelLoadPercent,
          xp,
          xpRequired,
          xpPercent: Math.min(100, Math.round((xp / xpRequired) * 100)),
          isPersonnelUpgradeRunning: Boolean(building.augmentation_personnel_en_cours),
          isVehicleUpgradeRunning: Boolean(building.augmentation_vehicules_en_cours),
        };
      })
      .sort((a, b) => {
        if (b.level !== a.level) return b.level - a.level;
        return a.name.localeCompare(b.name, 'fr');
      });
  }, [buildings, playerVehicles, playerPersonnel]);

  const buildingTypeBreakdown = useMemo(() => {
    const grouped: Record<
      string,
      { typeName: string; count: number; vehicles: number; personnel: number }
    > = {};
    buildingOperationalRows.forEach((row) => {
      const current = grouped[row.typeLabel] || {
        typeName: row.typeLabel,
        count: 0,
        vehicles: 0,
        personnel: 0,
      };
      current.count += 1;
      current.vehicles += row.vehiclesCount;
      current.personnel += row.personnelCount;
      grouped[row.typeLabel] = current;
    });
    return Object.values(grouped).sort(
      (a, b) => b.count - a.count || a.typeName.localeCompare(b.typeName, 'fr'),
    );
  }, [buildingOperationalRows]);

  const resourceAlerts = useMemo(() => {
    const alerts: string[] = [];
    if (
      vehicleStatusMetrics.mission >=
      Math.max(3, Math.ceil(vehicleStatusMetrics.total * 0.35))
    ) {
      alerts.push(
        'Charge terrain elevee : une part importante de la flotte est deja engagee.',
      );
    }
    if (
      vehicleStatusMetrics.maintenance + vehicleStatusMetrics.panne >=
      Math.max(2, Math.ceil(vehicleStatusMetrics.total * 0.25))
    ) {
      alerts.push(
        'Tension maintenance : plusieurs vehicules sont indisponibles (maintenance/panne).',
      );
    }
    if (personnelMetrics.nearRetirement > 0) {
      alerts.push(
        `${personnelMetrics.nearRetirement} agent(s) proche(s) de la retraite : anticiper la releve RH.`,
      );
    }
    if (activeInterventions.length >= 5) {
      alerts.push("Pic d activite : volume d interventions eleve sur la carte.");
    }
    return alerts.slice(0, 4);
  }, [vehicleStatusMetrics, personnelMetrics, activeInterventions.length]);

  const topMileageVehicles = useMemo(
    () =>
      [...playerVehicles]
        .sort((a, b) => Number(b.kilometrage || 0) - Number(a.kilometrage || 0))
        .slice(0, 5),
    [playerVehicles],
  );

  const buildingFilterOptions = useMemo(
    () =>
      buildings
        .filter((b) => !isCtaBuildingType(b.type))
        .map((b) => ({ id: Number(b.id), name: b.nom || `Bâtiment ${b.id}` }))
        .sort((a, b) => a.name.localeCompare(b.name, 'fr')),
    [buildings],
  );

  const vehicleTypeFilterOptions = useMemo(() => {
    const types = Array.from(
      new Set(playerVehicles.map((v) => String(v.type || '').trim()).filter((v) => v.length > 0)),
    );
    types.sort((a, b) => a.localeCompare(b, 'fr'));
    return types;
  }, [playerVehicles]);


  const selectedInterventionVehicleRows = useMemo(() => {
    if (!selectedIntervention?.dispatch_player_vehicle_ids?.length) return [];
    const ids = new Set(
      selectedIntervention.dispatch_player_vehicle_ids.map((x) => Number(x)),
    );
    return playerVehicles.filter((v) => ids.has(Number(v.id)));
  }, [selectedIntervention, playerVehicles]);

  return {
    vehicleStatusMetrics,
    personnelMetrics,
    buildingOperationalRows,
    buildingTypeBreakdown,
    resourceAlerts,
    topMileageVehicles,
    buildingFilterOptions,
    vehicleTypeFilterOptions,
    selectedInterventionVehicleRows,
  };
}
